/*
French resource file — the canonical reference. The English and
Spanish files mirror the same key shape; whenever a key is added
here, add it in en.ts and es.ts at the same time (TypeScript will
catch missing keys at build time once the namespace types are
registered, but for now keep the three in sync manually).
*/

export const fr =
{
	common:
	{
		loading:			"Chargement…",
		error_network:		"Erreur réseau.",
		error_loading:		"Erreur de chargement : {{message}}",
		never:				"jamais",
		none:				"aucun",
		dash:				"—",
		yes:				"Oui",
		no:					"Non",
		cancel:				"Annuler",
		confirm:			"Confirmer",
		save:				"Enregistrer",
		update:				"Mettre à jour",
		updating:			"Mise à jour…",
		send:				"Envoyer",
		sending:			"Envoi…",
		clear:				"Effacer",
		prev:				"← Précédent",
		next:				"Suivant →",
		page_of:			"Page {{page}} / {{total}}",
		rows_per_page:		"Lignes par page",
		page_size_label:	"Nombre de lignes par page",
		download:			"Télécharger",
		downloading:		"Téléchargement…",
		retry:				"Réessayer",
		error_state_title:	"Une erreur est survenue",
		from:				"De",
		to:					"À",
	},

	nav:
	{
		app_name:			"SpirApps",
		my_cards:			"Mes cartes",
		shared_with_me:		"Partagés avec moi",
		my_account:			"Mon compte",
		statistics:			"Statistiques",
		users:				"Utilisateurs",
		audit:				"Audit",
		logout:				"Se déconnecter",
		copyright:			"© Spirtech 2026",
	},

	auth:
	{
		login_title:					"SpirApps",
		login_subtitle_passwordless:	"Saisissez votre adresse courriel ; un code de connexion à 6 chiffres vous sera envoyé.",
		email_label:					"Courriel",
		code_label:						"Code à 6 chiffres",
		send_code_submit:				"Envoyer le code",
		login_submit:					"Se connecter",
		login_progress:					"Connexion…",
		code_sent_info:					"Si un compte existe pour cette adresse, un code vient de vous être envoyé. Vérifiez votre boîte de réception (et vos spams). Le code est valable 1 heure.",
		code_resent_info:				"Un nouveau code vient de vous être envoyé. Le précédent n'est plus valable.",
		code_step_subtitle:				"Saisissez le code reçu à l'adresse {{email}}.",
		change_email_link:				"Utiliser un autre courriel",
		resend_code_link:				"Renvoyer le code",

		err_invalid_credentials:	"Code incorrect ou expiré.",
		err_account_disabled:		"Compte désactivé. Contactez l'administrateur.",
		err_missing_field:			"Veuillez renseigner tous les champs.",
		err_rate_limited:			"Trop de tentatives. Réessayez dans quelques minutes.",
		err_default:				"Erreur de connexion. Réessayez.",
		session_expired_banner:		"Session expirée. Veuillez vous reconnecter.",
	},

	cards:
	{
		title:					"Mes cartes",
		title_count:			"Mes cartes ({{total}})",
		no_cards:				"Aucune carte téléversée pour le moment.",
		no_cards_with_filters:	"Aucune carte ne correspond aux filtres.",
		col_serial:				"Numéro de série",
		col_type:				"Type",
		col_model:				"Modèle",
		col_folder:				"Dossier",
		col_read_on:			"Lue le",
		filter_serial:			"Numéro de série…",
		filter_type:			"Type (ex. CALYPSO_PRIME)",
		filter_type_all:		"Tous les types",
		filter_model:			"Modèle (ex. INTERCODE_2_2)",
		filter_model_all:		"Tous les modèles",
		filter_folder:			"Dossier (préfixe)",
		filter_date_min:		"Date de lecture min",
		filter_date_max:		"Date de lecture max",
		delete_tooltip:			"Supprimer",
		delete_aria:			"Supprimer la carte {{serial}}",
		delete_dialog_title:	"Supprimer la carte ?",
		delete_dialog_body:		"Cette action est irréversible.",
		delete_dialog_button:	"Supprimer",
		export_csv:				"Exporter CSV",
		export_xlsx:			"Exporter Excel",
	},

	compare:
	{
		title:				"Comparaison de deux cartes",
		section_title:		"Comparer cette carte",
		button:				"Comparer avec…",
		picker_title:		"Choisir la carte à comparer",
		picker_search:		"Numéro de série…",
		picker_empty:		"Aucune autre carte à comparer.",
		picker_cancel:		"Annuler",
		card_type:			"Type",
		data_model:			"Modèle",
		read_date:			"Lue le",
		folder:				"Dossier",
		section_select:		"SELECT",
		section_sv:			"SV",
		section_environment:"Environnement",
		section_contracts:	"Contrats",
		section_events:		"Événements",
		field:				"Champ",
		value_a:			"Valeur A",
		value_b:			"Valeur B",
		missing_ids:		"Lien de comparaison incomplet (paramètres a / b manquants).",
	},

	card_detail:
	{
		back_to_list:				"← Retour à la liste",
		read_on:					"{{type}} — {{model}} — lue le {{date}}",
		downloads_title:			"Téléchargements",
		downloads_derived:			"(dérivé)",
		downloads_error_status:		"Erreur {{status}}: {{message}}",
		downloads_error_network:	"Erreur réseau lors du téléchargement.",
		parts_bundle_title:			"Parts du bundle",
		parts_col_name:				"Nom",
		parts_col_type:				"Type",
		parts_col_file:				"Fichier",
		parts_col_received:			"Reçu le",
		raw_metadata_title:			"Métadonnée brute",
		share_title:				"Partage",
		share_email_placeholder:	"Adresse courriel du destinataire",
		share_message_placeholder:	"Message (optionnel)",
		share_submit:				"Partager",
		share_progress:				"Partage…",
		shares_col_recipient:		"Destinataire",
		shares_col_shared_on:		"Partagé le",
		shares_col_viewed:			"Vu",
		shares_col_message:			"Message",
		no_active_shares:			"Aucun partage actif pour cette carte.",
		revoke_button:				"Révoquer",
		revoke_progress:			"Révocation…",
		revoke_dialog_title:		"Révoquer le partage ?",
		revoke_dialog_body:			"Le destinataire perdra immédiatement l'accès à cette carte.",
		revoke_error:				"Erreur lors de la révocation.",
		share_err_missing_field:	"Champs obligatoires manquants ou courriel invalide.",
		share_err_recipient:		"Aucun utilisateur ne correspond à ce courriel.",
		share_err_self:				"Vous ne pouvez pas partager avec vous-même.",
		share_err_forbidden:		"Accès refusé.",
		share_err_default:			"Erreur ({{status}}).",
	},

	profile:
	{
		title:				"Mon compte",
		field_email:		"Courriel",
		field_role:			"Rôle",
		field_created_on:	"Compte créé le",
		field_last_login:	"Dernière connexion",
		field_last_lockout:	"Dernier verrouillage automatique",
		lockout_warning:	"Si ce verrouillage ne provenait pas de vous, contactez l'administrateur.",
		email_form_title:	"Changer mon courriel",
		new_email_label:	"Nouvelle adresse",
		email_same_as_current:	"La nouvelle adresse est identique à l'actuelle.",
		email_updated:		"Adresse mise à jour.",
		err_email_taken:	"Cette adresse est déjà utilisée par un autre compte.",
		err_missing_field:	"Champs obligatoires manquants ou invalides.",
		err_unauthenticated:	"Session expirée. Veuillez vous reconnecter.",
		err_default:		"Erreur ({{status}}).",
	},

	shared:
	{
		title_count:			"Partagés avec moi ({{total}})",
		no_shares:				"Personne ne vous a partagé de carte pour le moment.",
		col_serial:				"Numéro de série",
		col_shared_by:			"Partagé par",
		col_received_on:		"Reçu le",
		col_type:				"Type",
		col_model:				"Modèle",
		col_message:			"Message",
		new_badge:				"Nouveau",
	},

	admin_users:
	{
		title_count:				"Utilisateurs ({{total}})",
		search_placeholder:			"Rechercher par courriel…",
		col_email:					"Courriel",
		col_role:					"Rôle",
		col_cards:					"Cartes",
		col_created:				"Créé",
		col_status:					"État",
		you:						"(vous)",
		status_active:				"actif",
		status_disabled:			"désactivé",
		status_locked:				"verrouillé",
		locked_until_tooltip:		"Verrouillé jusqu'à {{date}} {{time}}",
		btn_promote:				"→ Admin",
		btn_demote:					"→ Utilisateur",
		btn_disable:				"Désactiver",
		btn_reactivate:				"Réactiver",
		btn_unlock:					"Déverrouiller",
		btn_delete:					"Supprimer",
		btn_resend_welcome:			"Renvoyer l'invitation",
		welcome_resent_ok:			"Courriel d'invitation renvoyé à {{email}}.",
		welcome_resent_err:			"Échec de l'envoi de l'invitation à {{email}}.",
		delete_dialog_title:		"Supprimer l'utilisateur ?",
		delete_dialog_body:			"Toutes les cartes de cet utilisateur seront également supprimées. Cette action est irréversible.",
		create_title:				"Créer un utilisateur",
		create_email:				"Courriel",
		create_role:				"Rôle",
		create_submit:				"Créer",
		create_progress:			"Création…",
	},

	audit:
	{
		title_count:			"Journal d'audit ({{total}})",
		filter_action_all:		"Toutes les actions",
		filter_actor:			"Acteur (courriel)…",
		filter_date_min:		"Date min",
		filter_date_max:		"Date max",
		no_entries:				"Aucune entrée.",
		no_entries_filtered:	"Aucune entrée ne correspond aux filtres.",
		col_when:				"Quand",
		col_action:				"Action",
		col_actor:				"Acteur",
		col_ip:					"IP",
		col_target:				"Cible",
		col_details:			"Détails",
		actor_now_suffix:		"(now {{email}})",
		actor_deleted_suffix:	"(supprimé)",
		actor_deleted_tooltip:	"Compte supprimé",
	},

} as const;
